import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Download, CheckCircle2, FileText } from 'lucide-react';

interface Detail {
  client: {
    id: number; business_name: string; contact_name: string | null;
    contact_email: string | null; contact_phone: string | null;
    onboarding_status: string; status: string;
  };
  profile: Record<string, unknown> | null;
  files: { id: number; category: string; label: string | null; r2_key: string; filename: string; size: number | null; created_at: string }[];
  agreements: { id: number; doc_title: string; doc_version: string; accepted_name: string; ip_address: string | null; accepted_at: string; email_sent: number }[];
}

async function fetchDetail(id: string): Promise<Detail> {
  const res = await fetch(`/api/onboarding/${id}`, { credentials: 'same-origin' });
  if (res.status === 401) { window.location.href = '/login'; throw new Error('unauthorized'); }
  const data = await res.json() as Detail & { error?: string };
  if (!res.ok) throw new Error(data.error || 'error');
  return data;
}

export default function AdminOnboardingDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin-onboarding', id], queryFn: () => fetchDetail(id), enabled: !!id });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={28} /></div>;
  if (isError || !data) return <p className="text-rose-600">Error al cargar.</p>;

  const { client, profile, files, agreements } = data;
  const pdata = (profile?.data ?? {}) as Record<string, unknown>;

  return (
    <div className="max-w-4xl space-y-6">
      <Link to="/onboarding" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft size={16} /> Volver a altas
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">{client.business_name}</h1>
        <p className="text-slate-500">{client.contact_name} · {client.contact_email} · {client.contact_phone}</p>
      </div>

      {/* Agreement */}
      <Card title="Acuerdo de servicio">
        {agreements.length === 0 ? (
          <p className="text-slate-400 text-sm">Aún no firmado.</p>
        ) : agreements.map(a => (
          <div key={a.id} className="flex items-center gap-3 text-sm py-1">
            <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
            <span className="text-slate-700">
              <strong>{a.accepted_name}</strong> — {a.doc_title} (v{a.doc_version})
              · {new Date(a.accepted_at).toLocaleString('es-ES')}
              {a.ip_address && <span className="text-slate-400"> · IP {a.ip_address}</span>}
              {a.email_sent ? <span className="text-emerald-600"> · copia enviada</span> : null}
            </span>
          </div>
        ))}
      </Card>

      {/* Profile */}
      <Card title="Datos del alojamiento">
        {!profile ? (
          <p className="text-slate-400 text-sm">Sin datos todavía.</p>
        ) : (
          <div className="space-y-4">
            <FieldGrid rows={[
              ['Razón social', profile.legal_name], ['NIF/CIF', profile.tax_id],
              ['Dirección fiscal', profile.billing_address], ['Teléfono', profile.contact_phone],
              ['WhatsApp', profile.contact_whatsapp], ['Nº propiedades', profile.property_count],
              ['Tipo', profile.property_type], ['Estado', profile.status],
            ]} />
            <details className="rounded-lg border border-slate-200">
              <summary className="px-4 py-2.5 text-sm font-medium text-slate-700 cursor-pointer">Detalle completo (JSON)</summary>
              <pre className="px-4 py-3 text-xs text-slate-600 overflow-x-auto bg-slate-50 rounded-b-lg">{JSON.stringify(pdata, null, 2)}</pre>
            </details>
          </div>
        )}
      </Card>

      {/* Files */}
      <Card title={`Archivos (${files.length})`}>
        {files.length === 0 ? (
          <p className="text-slate-400 text-sm">Sin archivos.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {files.map(f => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <FileText size={16} className="text-slate-400 shrink-0" />
                <span className="flex-1 truncate text-slate-700">{f.filename} <span className="text-slate-400">· {f.category}</span></span>
                <a href={`/api/storage/file?key=${encodeURIComponent(f.r2_key)}`}
                  className="text-teal-600 hover:text-teal-700 flex items-center gap-1" download>
                  <Download size={15} /> Descargar
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="font-semibold text-slate-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}

function FieldGrid({ rows }: { rows: [string, unknown][] }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 border-b border-slate-50 py-1">
          <span className="text-slate-500">{label}</span>
          <span className="text-slate-800 text-right">{value != null && value !== '' ? String(value) : '—'}</span>
        </div>
      ))}
    </div>
  );
}
